import axios from "axios";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

// --- Passive Health Beacon (Feb 2026) ---
// When the app hits a 5xx from the backend, fire a small telemetry post so
// the team hears about it before the next user email lands. Deduplicates
// bursts of the same error within 5s to avoid flooding.
const _lastBeacon = new Map(); // key -> ts
const _fireBeacon = (payload) => {
  try {
    const key = `${payload.kind}|${payload.path}|${payload.status}|${payload.message?.slice(0, 80)}`;
    const now = Date.now();
    if (_lastBeacon.get(key) && now - _lastBeacon.get(key) < 5000) return;
    _lastBeacon.set(key, now);
    // sendBeacon works even when the tab is closing; fall back to fetch.
    const body = JSON.stringify(payload);
    const url = `${API}/health/beacon`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
    // Also tag the PostHog session so a saved view surfaces crashes with replay.
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture(`pb_${payload.kind}`, {
        status: payload.status,
        path: payload.path,
        message: payload.message,
        url: payload.url,
      });
      // Mark the whole session so the PostHog "Sessions with render errors" saved view lights up.
      window.posthog.people?.set?.({ pb_last_error_at: new Date().toISOString(), pb_last_error_kind: payload.kind });
    }
  } catch { /* never let telemetry throw */ }
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    // Normalise error payloads so raw Cloudflare/HTML/plaintext bodies
    // don't leak into user-facing toasts. Every catch site in the app uses
    // `err.response?.data?.detail || "Failed"` — populate a friendly `detail`
    // when the origin returned HTML, a timeout, or a network drop.
    try {
      const data = err?.response?.data;
      const isHtml = typeof data === "string" && /<html|cloudflare|<!doctype/i.test(data);
      const looksLikeCloudflare = isHtml || (typeof data === "string" && /cloudflare|origin web server/i.test(data));
      if (looksLikeCloudflare) {
        // Overwrite with a friendly, actionable message.
        err.response.data = { detail: "The server hiccuped. Please retry — if it keeps happening, refresh the page." };
      } else if (typeof data === "string" && !data.trim().startsWith("{")) {
        // Any other non-JSON body: keep it if it's short & human, else redact.
        err.response.data = { detail: data.length < 160 ? data : "Something went wrong. Please try again." };
      } else if (status === 413) {
        // Only supply a friendly default when the backend didn't send a detail.
        if (!data || typeof data !== "object" || !data.detail) {
          err.response.data = { detail: "This file is too large — try under 50 MB." };
        }
      } else if (status === 429) {
        // Only supply a friendly default when the backend didn't send a detail.
        if (!data || typeof data !== "object" || !data.detail) {
          err.response.data = { detail: "You're going a bit fast — please wait a moment and retry." };
        }
      } else if (!err.response && err.code === "ECONNABORTED") {
        // Timeout on the client side.
        err.response = { data: { detail: "Request timed out. Check your connection and retry." } };
      } else if (!err.response) {
        // Network drop — no response received.
        err.response = { data: { detail: "You appear to be offline. Reconnect and try again." } };
      }
    } catch { /* never let normalisation throw */ }
    if (status && status >= 500) {
      _fireBeacon({
        kind: "http_5xx",
        status,
        path: err.config?.url,
        message: err.response?.data?.detail || err.message,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      });
    }
    // 402 with a structured `{error:"cap_reached", feature, plan, cap, used, message}`
    // envelope → dispatch a window event so the global UpsellModal can render
    // without every catch site needing bespoke code. Individual callers can
    // still swallow the error normally after we notify.
    try {
      const d = err?.response?.data?.detail;
      if (status === 402 && d && typeof d === "object" && d.error === "cap_reached") {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("pb:cap-reached", { detail: d }));
        }
      }
    } catch { /* never let interceptor throw */ }
    return Promise.reject(err);
  }
);

// Catch unhandled promise rejections + top-level errors at window scope.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    _fireBeacon({
      kind: "unhandled_rejection",
      message: String(e?.reason?.message || e?.reason || "").slice(0, 400),
      stack: String(e?.reason?.stack || "").slice(0, 2000),
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
  });
  window.addEventListener("error", (e) => {
    _fireBeacon({
      kind: "window_error",
      message: String(e?.message || "").slice(0, 400),
      stack: String(e?.error?.stack || "").slice(0, 2000),
      url: window.location.href,
      user_agent: navigator.userAgent,
    });
  });
}

// Public helper so ErrorBoundary can also emit render errors.
export const reportRenderError = (err, info) => _fireBeacon({
  kind: "render_error",
  message: String(err?.message || err || "").slice(0, 400),
  stack: String(err?.stack || info?.componentStack || "").slice(0, 2000),
  url: typeof window !== "undefined" ? window.location.href : undefined,
  user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
});

export const photoUrl = (photoId) => `${API}/photos/${photoId}/file`;
// Small WebP thumbnail for grid tiles — 30–50× smaller than full-res.
// `crop=1&ar=1` returns a face-safe square crop centered on the photo's focal
// point (or auto-detected face bbox) so no face ever gets clipped by client-
// side `object-fit: cover`. Callers that want the original letterboxed thumb
// omit the `crop` arg.
export const photoThumbUrl = (photoId, size = 320, opts = {}) => {
  const q = new URLSearchParams({ size: String(size) });
  if (opts.crop) { q.set("crop", "1"); if (opts.ar) q.set("ar", String(opts.ar)); }
  return `${API}/photos/${photoId}/thumb?${q}`;
};
// Responsive srcSet for the authenticated photo-thumb endpoint. Emits 320/640/960/1280/1920w
// candidates so retina laptops, high-DPI phones, and 4K TVs each pick the sharpest fit.
export const photoThumbSrcSet = (photoId, opts = {}) =>
  [320, 640, 960, 1280, 1920].map(w => {
    const q = new URLSearchParams({ size: String(w) });
    if (opts.crop) { q.set("crop", "1"); if (opts.ar) q.set("ar", String(opts.ar)); }
    return `${API}/photos/${photoId}/thumb?${q} ${w}w`;
  }).join(", ");
export const galleryPhotoUrl = (token, photoId, password) =>
  `${API}/gallery/${token}/photo/${photoId}${password ? `?password=${encodeURIComponent(password)}` : ""}`;
export const galleryPhotoThumbUrl = (token, photoId, password, size = 480, opts = {}) => {
  const q = new URLSearchParams({ size: String(size) });
  if (password) q.set("password", password);
  if (opts.crop) { q.set("crop", "1"); if (opts.ar) q.set("ar", String(opts.ar)); }
  return `${API}/gallery/${token}/photo/${photoId}/thumb?${q}`;
};
// Responsive srcSet for the PUBLIC client-gallery thumb endpoint.
export const galleryPhotoThumbSrcSet = (token, photoId, password, opts = {}) =>
  [320, 640, 960, 1280, 1920].map(w => {
    const q = new URLSearchParams({ size: String(w) });
    if (password) q.set("password", password);
    if (opts.crop) { q.set("crop", "1"); if (opts.ar) q.set("ar", String(opts.ar)); }
    return `${API}/gallery/${token}/photo/${photoId}/thumb?${q} ${w}w`;
  }).join(", ");
